import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
} from '@angular/core';
import {
  CameraDevice,
  Html5Qrcode,
  Html5QrcodeSupportedFormats,
} from 'html5-qrcode';

const DEFAULT_FORMATS: Html5QrcodeSupportedFormats[] = [
  Html5QrcodeSupportedFormats.QR_CODE,
  Html5QrcodeSupportedFormats.AZTEC,
  Html5QrcodeSupportedFormats.CODE_39,
  Html5QrcodeSupportedFormats.CODE_128,
  Html5QrcodeSupportedFormats.DATA_MATRIX,
  Html5QrcodeSupportedFormats.EAN_13,
  Html5QrcodeSupportedFormats.EAN_8,
  Html5QrcodeSupportedFormats.ITF,
  Html5QrcodeSupportedFormats.PDF_417,
  Html5QrcodeSupportedFormats.RSS_14,
];

/** getUserMedia error names that mean the user or browser refused camera access. */
const PERMISSION_ERROR_NAMES = ['NotAllowedError', 'SecurityError'];

export type CameraState = 'requesting' | 'scanning' | 'blocked' | 'unavailable';

let nextInstanceId = 0;

@Component({
  selector: 'app-barcode-scanner',
  templateUrl: './barcode-scanner.component.html',
  styleUrls: ['./barcode-scanner.component.scss'],
  standalone: false,
})
export class BarcodeScannerComponent implements AfterViewInit, OnDestroy {
  @Input() formatsToSupport: Html5QrcodeSupportedFormats[] = DEFAULT_FORMATS;

  @Output() scanSuccess = new EventEmitter<string>();
  @Output() permissionResponse = new EventEmitter<boolean>();
  @Output() camerasFound = new EventEmitter<CameraDevice[]>();

  readonly elementId = `barcode-scanner-${nextInstanceId++}`;

  cameraState: CameraState = 'requesting';
  /**
   * True only when the browser will show its permission prompt again
   * (Permissions API reports `prompt`). Browsers never let a page reopen
   * a permission that is `denied`, so the button is hidden in that case.
   */
  canRequestPermission = false;

  private scanner: Html5Qrcode | null = null;
  private permissionStatus: PermissionStatus | null = null;
  private destroyed = false;

  private readonly onPermissionChange = () => this.handlePermissionChange();

  ngAfterViewInit(): void {
    this.watchPermission();
    this.startScanning();
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.permissionStatus?.removeEventListener(
      'change',
      this.onPermissionChange,
    );
    this.stopScanning();
  }

  enableCamera(): void {
    this.startScanning();
  }

  private async startScanning(): Promise<void> {
    if (this.cameraState === 'scanning') return;
    this.cameraState = 'requesting';

    try {
      const cameras = await Html5Qrcode.getCameras();
      if (this.destroyed) return;

      this.permissionResponse.emit(true);
      this.camerasFound.emit(cameras);
      if (!cameras.length) {
        this.cameraState = 'unavailable';
        return;
      }

      this.scanner ??= new Html5Qrcode(this.elementId, {
        formatsToSupport: this.formatsToSupport,
        verbose: false,
      });

      await this.scanner.start(
        { facingMode: 'environment' },
        { fps: 10 },
        (decodedText) => this.scanSuccess.emit(decodedText),
        undefined,
      );
      this.cameraState = 'scanning';

      if (this.destroyed) {
        this.stopScanning();
      }
    } catch (error) {
      if (this.destroyed) return;
      if (this.isPermissionError(error)) {
        this.permissionResponse.emit(false);
        this.cameraState = 'blocked';
        this.canRequestPermission = this.permissionStatus?.state === 'prompt';
      } else {
        this.cameraState = 'unavailable';
      }
    }
  }

  private async stopScanning(): Promise<void> {
    if (!this.scanner?.isScanning) return;
    try {
      await this.scanner.stop();
      this.scanner.clear();
    } catch {
      // scanner may already be stopped or torn down
    }
  }

  /**
   * Subscribes to camera permission changes so the scanner reacts when the
   * user changes the site permission while the dialog is open. Browsers
   * without camera support in the Permissions API (older Firefox) throw here;
   * those users only get the site-permissions guidance.
   */
  private async watchPermission(): Promise<void> {
    if (!navigator.permissions?.query) return;
    try {
      const status = await navigator.permissions.query({ name: 'camera' });
      if (this.destroyed) return;
      this.permissionStatus = status;
      status.addEventListener('change', this.onPermissionChange);
      if (this.cameraState === 'blocked') {
        this.canRequestPermission = status.state === 'prompt';
      }
    } catch {
      // camera is not a queryable permission in this browser
    }
  }

  private handlePermissionChange(): void {
    const state = this.permissionStatus?.state;
    if (state === 'granted' && this.cameraState === 'blocked') {
      this.startScanning();
      return;
    }
    if (this.cameraState === 'blocked') {
      this.canRequestPermission = state === 'prompt';
    }
  }

  private isPermissionError(error: unknown): boolean {
    return (
      error instanceof DOMException &&
      PERMISSION_ERROR_NAMES.includes(error.name)
    );
  }
}
